/**
 * Schmale Huelle um die Server-API.
 *
 * Grundsatz: Kein Netzfehler darf das Spiel unterbrechen. Jeder Aufruf faengt
 * seine Fehler ab und meldet sie als Wert zurueck, damit die Oberflaeche einen
 * Hinweis zeigen kann, statt abzustuerzen.
 */

import { API_BASE } from './config'
import type { Discipline, RunResult } from './engine/disciplines'
import type { DeviceMetrics } from './engine/dpi'

export interface SubmitPayload {
  discipline: Discipline
  variant: string | null
  distanceCm: number
  timeMs: number
  playerName: string
  clientId: string
  device: {
    ppi: number
    dpr: number
    confidence: string
    resolution: string
  }
  samples: { t: number; cm: number }[]
}

export interface LeaderboardEntry {
  rank: number
  playerName: string
  distanceCm: number
  timeMs: number
  createdAt: string
}

export interface SubmitResponse {
  runId: string
  rank: number | null
}

export interface ChallengeAttempt {
  playerName: string
  distanceCm: number
  timeMs: number
  createdAt: string
}

export interface Challenge {
  code: string
  discipline: Discipline
  variant: string | null
  challenger: ChallengeAttempt
  attempts: ChallengeAttempt[]
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string }

export function buildPayload(
  result: RunResult,
  playerName: string,
  clientId: string,
  metrics: DeviceMetrics,
): SubmitPayload {
  return {
    discipline: result.discipline,
    variant: result.variant,
    distanceCm: Number(result.distanceCm.toFixed(2)),
    timeMs: result.timeMs,
    playerName,
    clientId,
    device: {
      ppi: Number(metrics.ppi.toFixed(1)),
      dpr: metrics.dpr,
      confidence: metrics.confidence,
      resolution: metrics.resolutionKey,
    },
    samples: result.samples,
  }
}

export function submitRun(payload: SubmitPayload): Promise<ApiResult<SubmitResponse>> {
  return request<SubmitResponse>('/runs', { method: 'POST', body: payload })
}

export function fetchLeaderboard(
  discipline: Discipline,
  variant: string | null,
): Promise<ApiResult<LeaderboardEntry[]>> {
  const params = new URLSearchParams({ discipline })
  if (variant) params.set('variant', variant)
  return request<LeaderboardEntry[]>(`/leaderboard?${params.toString()}`)
}

export function createChallenge(payload: SubmitPayload): Promise<ApiResult<{ code: string }>> {
  return request<{ code: string }>('/challenges', { method: 'POST', body: payload })
}

export function fetchChallenge(code: string): Promise<ApiResult<Challenge>> {
  return request<Challenge>(`/challenges/${encodeURIComponent(code)}`)
}

export interface ChallengeSummary {
  code: string
  discipline: Discipline
  variant: string | null
  attempts: number
  bestName: string | null
  bestDistanceCm: number | null
  bestTimeMs: number | null
}

/** Kurzstand aller gemerkten Duelle in einem Aufruf. */
export function fetchChallengeSummaries(
  codes: string[],
): Promise<ApiResult<ChallengeSummary[]>> {
  if (codes.length === 0) return Promise.resolve({ ok: true, data: [] })
  return request<ChallengeSummary[]>(`/challenges/summary?codes=${codes.join(',')}`)
}

export function submitChallengeAttempt(
  code: string,
  payload: SubmitPayload,
): Promise<ApiResult<Challenge>> {
  return request<Challenge>(`/challenges/${encodeURIComponent(code)}/attempts`, {
    method: 'POST',
    body: payload,
  })
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<ApiResult<T>> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? 'GET',
      headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
      body: options.body ? JSON.stringify(options.body) : undefined,
    })

    if (!response.ok) {
      const detail = await readError(response)
      return { ok: false, error: detail }
    }
    return { ok: true, data: (await response.json()) as T }
  } catch {
    return { ok: false, error: 'Keine Verbindung zum Server.' }
  }
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    if (body.error) return body.error
  } catch {
    // Antwort war kein JSON - dann reicht der Statuscode.
  }
  if (response.status === 429) return 'Zu viele Versuche in kurzer Zeit. Kurz warten.'
  return `Server antwortet mit Fehler ${response.status}.`
}

/** Vollstaendiger Link zu einem Duell, zum Teilen. */
export function challengeUrl(code: string): string {
  return `${window.location.origin}/duell/${code}`
}
